// src/components/common/taskbox/TaskBox.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { RefreshCw, CheckCircle, User, Users } from 'lucide-react';
import './TaskBox.css';

// Import widgets
import CreateTaskWidget from './CreateTaskWidget';
import OnboardingWidget from './OnboardingWidget';
import OnboardingProgressWidget from './OnboardingProgressWidget';
import RecentActivitiesWidget from './RecentActivitiesWidget';
import UpcomingActivitiesWidget from './UpcomingActivitiesWidget';
import TasksWidget from './TasksWidget';
import StatsWidget from './StatsWidget';
import TeamTasksWidget from './TeamTasksWidget';

// Import services
import taskService from '../../../services/task.service';
import activityService from '../../../services/activity.service';
import taskPerformanceService from '../../../services/taskPerformance.service';
import teamTasksService from '../../../services/teamTasks.service';

// Import contexts
import { useAuth } from '../../../hooks/useAuth';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { useEmployees } from '../../../contexts/EmployeeContext';

const TaskBox = ({ userRole = 'employee' }) => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const { onboardingData: contextOnboardingData } = useOnboarding();
    const { employees } = useEmployees();
    const [searchParams] = useSearchParams();

    // Check URL parameter for view type (e.g., ?view=team)
    const viewParam = searchParams.get('view');
    const initialTaskType = viewParam === 'team' ? 'team' : 'personal';

    const [activeView, setActiveView] = useState('daily'); // daily, weekly, monthly, quarterly, annual
    const [taskType, setTaskType] = useState(initialTaskType); // personal, team
    const [refreshKey, setRefreshKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showCongrats, setShowCongrats] = useState(false);

    // State for different data types
    const [tasks, setTasks] = useState([]);
    const [teamTasks, setTeamTasks] = useState([]);
    const [activities, setActivities] = useState({ recent: [], upcoming: [] });
    const [stats, setStats] = useState(null);

    // Check if user is fully onboarded
    const currentEmployee = employees.find(emp => emp.email === user?.email) || user;
    const employeeId = currentEmployee?.id;
    const isFullyOnboarded = employeeId && contextOnboardingData
        ? contextOnboardingData[employeeId]?.adminReviewed === true
        : false;

    // Static layout - no drag/resize functionality
    // Heights will be auto-adjusted by CSS based on content

    useEffect(() => {
        loadAllData();

        // Check and run EOD cleanup for daily tasks (once per day)
        taskService.checkAndRunEODCleanup();
    }, [refreshKey, activeView, taskType]);

    const loadAllData = async () => {
        setLoading(true);
        try {
            if (taskType === 'personal') {
                await Promise.all([
                    loadTasks(),
                    loadActivities(),
                    loadStats()
                ]);
            } else {
                await Promise.all([
                    loadTeamTasks(),
                    loadTeamActivities(),
                    loadTeamStats()
                ]);
            }
            // Note: Onboarding data is handled by OnboardingProgressWidget via context
        } catch (error) {
            console.error('Error loading TaskBox data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadTasks = async () => {
        try {
            // Load personal tasks only (exclude team tasks)
            const response = await taskService.getTasks({
                type: activeView,
                // No status filter - get all tasks
            });

            // Filter out team tasks (tasks with team_task_id or is_team_task flag)
            const personalTasks = (response.data || []).filter(task =>
                !task.team_task_id && !task.is_team_task
            );

            setTasks(personalTasks);
        } catch (error) {
            console.error('Error loading tasks:', error);
        }
    };

    const loadActivities = async () => {
        try {
            const [recentRes, upcomingRes] = await Promise.all([
                taskService.getRecentActivities(10),  // Real-time task activities
                taskService.getUpcomingTasks(5)        // Upcoming tasks by deadline
            ]);

            // Filter out team task activities for personal view
            const personalRecent = (recentRes.data || []).filter(activity =>
                !activity.is_team_task && !activity.team_task_id
            );
            const personalUpcoming = (upcomingRes.data || []).filter(task =>
                !task.is_team_task && !task.team_task_id
            );

            setActivities({
                recent: personalRecent,
                upcoming: personalUpcoming
            });
        } catch (error) {
            console.error('Error loading activities:', error);
        }
    };

    const loadStats = async () => {
        try {
            const response = await taskPerformanceService.getStats(activeView);
            setStats(response.data || null);
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const loadTeamTasks = async () => {
        try {
            console.log('🔍 Loading team tasks for employee:', user?.id);
            // Use backend filtering with view_type=my_tasks to get only tasks assigned to this employee
            const response = await teamTasksService.getTeamTasks({
                view_type: 'my_tasks'
            });
            setTeamTasks(response.data || []);
            console.log('✅ Loaded team tasks:', response.data?.length || 0);
        } catch (error) {
            console.error('Error loading team tasks:', error);
            setTeamTasks([]);
        }
    };

    const loadTeamActivities = async () => {
        try {
            // For team tasks, activities are based on team task updates
            const [recentRes, upcomingRes] = await Promise.all([
                taskService.getRecentActivities(10),
                taskService.getUpcomingTasks(5)
            ]);
            setActivities({
                recent: recentRes.data?.filter(activity =>
                    activity.is_team_task || teamTasks.some(task => task.id === activity.task_id)
                ) || [],
                upcoming: upcomingRes.data?.filter(task =>
                    task.is_team_task && (task.assigned_employee_ids?.includes(user?.id) || task.created_by === user?.id)
                ) || []
            });
        } catch (error) {
            console.error('Error loading team activities:', error);
            setActivities({ recent: [], upcoming: [] });
        }
    };

    const loadTeamStats = async () => {
        try {
            // Calculate stats based on team tasks
            const completedTasks = teamTasks.filter(task => task.status === 'completed').length;
            const totalTasks = teamTasks.length;
            const pendingTasks = teamTasks.filter(task => task.status === 'pending').length;
            const inProgressTasks = teamTasks.filter(task => task.status === 'in_progress').length;

            setStats({
                completed: completedTasks,
                total: totalTasks,
                pending: pendingTasks,
                inProgress: inProgressTasks,
                completionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0
            });
        } catch (error) {
            console.error('Error loading team stats:', error);
            setStats(null);
        }
    };

    const handleTaskCreated = () => {
        setRefreshKey(prev => prev + 1);
    };

    const handleTaskCompleted = async (taskId) => {
        try {
            await taskService.completeTask(taskId);
            setRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error('Error completing task:', error);
        }
    };


    if (loading && refreshKey === 0) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
                <div className="text-center">
                    <RefreshCw className={`w-12 h-12 animate-spin mx-auto mb-4 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        Loading TaskBox...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className="p-6 space-y-6">
                {/* Congrats Modal */}
                {showCongrats && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
                        <div className={`rounded-xl p-8 max-w-md w-full mx-4 text-center shadow-2xl ${
                            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                        }`}>
                            <div className="text-6xl mb-4 animate-bounce">🎉</div>
                            <h2 className={`text-2xl font-bold mb-3 ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Congratulations!
                            </h2>
                            <p className={`mb-6 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                                You've completed all tasks in your roster!
                            </p>
                            <button
                                onClick={() => setShowCongrats(false)}
                                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                            >
                                Awesome!
                            </button>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-4 mb-2">
                            <h1 className={`text-3xl font-bold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Task Box
                            </h1>
                            {/* Personal/Team Switch */}
                            <div className={`flex items-center p-1 rounded-lg border ${
                                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'
                            }`}>
                                <button
                                    onClick={() => setTaskType('personal')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                                        taskType === 'personal'
                                            ? 'bg-blue-600 text-white'
                                            : theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    <User className="w-4 h-4" />
                                    Personal
                                </button>
                                <button
                                    onClick={() => setTaskType('team')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                                        taskType === 'team'
                                            ? 'bg-blue-600 text-white'
                                            : theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    <Users className="w-4 h-4" />
                                    Team
                                </button>
                            </div>
                        </div>
                        <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            {taskType === 'personal'
                                ? 'Manage your personal tasks, activities, and track your progress'
                                : 'View and manage team tasks assigned to you'
                            }
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {taskType === 'personal' && (
                            <select
                                value={activeView}
                                onChange={(e) => setActiveView(e.target.value)}
                                className={`px-4 py-2 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-800 border-gray-700 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            >
                                <option value="daily">Daily Tasks</option>
                                <option value="weekly">Weekly Tasks</option>
                                <option value="monthly">Monthly Tasks</option>
                                <option value="quarterly">Quarterly Tasks</option>
                                <option value="annual">Annual Tasks</option>
                            </select>
                        )}
                        <button
                            onClick={() => setRefreshKey(prev => prev + 1)}
                            disabled={loading}
                            className={`p-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                                    : 'bg-white border-gray-300 hover:bg-gray-50'
                            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Refresh data"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Loading indicator */}
                {loading && refreshKey > 0 && (
                    <div className={`p-3 rounded-lg border ${
                        theme === 'dark' ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
                    }`}>
                        <div className="flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                            <span className={`text-sm ${
                                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                            }`}>
                                Refreshing data...
                            </span>
                        </div>
                    </div>
                )}

                {/* Static Grid Layout - No drag/resize */}
                <div className="taskbox-grid">
                    {taskType === 'personal' ? (
                        <>
                            {/* Row 1: Recent and Upcoming Activities */}
                            <div className="taskbox-row">
                                <div className="taskbox-widget">
                                    <RecentActivitiesWidget activities={activities.recent} theme={theme} />
                                </div>
                                <div className="taskbox-widget">
                                    <UpcomingActivitiesWidget activities={activities.upcoming} theme={theme} />
                                </div>
                            </div>

                            {/* Row 2: Onboarding Progress (conditional) */}
                            {!isFullyOnboarded && (
                                <div className="taskbox-row">
                                    <div className="taskbox-widget taskbox-widget-full">
                                        <OnboardingProgressWidget />
                                    </div>
                                </div>
                            )}

                            {/* Row 3: Create Task + Daily Tasks | Performance Stats */}
                            <div className="taskbox-row">
                                <div className="taskbox-column">
                                    <div className="taskbox-widget taskbox-widget-compact">
                                        <CreateTaskWidget
                                            onTaskCreated={handleTaskCreated}
                                            userRole={userRole}
                                            theme={theme}
                                        />
                                    </div>
                                    <div className="taskbox-widget">
                                        <TasksWidget
                                            tasks={tasks}
                                            activeView={activeView}
                                            onTaskCompleted={handleTaskCompleted}
                                            onRefresh={() => setRefreshKey(prev => prev + 1)}
                                            theme={theme}
                                        />
                                    </div>
                                </div>
                                <div className="taskbox-widget">
                                    <StatsWidget stats={stats} period={activeView} theme={theme} />
                                </div>
                            </div>
                        </>
                    ) : (
                        /* Team Tasks Layout */
                        <div className="taskbox-row">
                            <div className="taskbox-widget taskbox-widget-full">
                                <TeamTasksWidget
                                    teamTasks={teamTasks}
                                    activities={activities}
                                    stats={stats}
                                    onRefresh={() => setRefreshKey(prev => prev + 1)}
                                    theme={theme}
                                    user={user}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaskBox;
